// Adapted from Hedge-Fund-Tracking-System apps/web/src/components/layout/sidebar.tsx.
// Same structure (logo header, grouped nav, collapse toggle). Swaps:
//   next/link        → react-router-dom Link
//   usePathname()    → useLocation().pathname
//   role-based filtering + fund context → dropped (we have a single bot)
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  ArrowLeftRight,
  Layers,
  Settings,
  ChevronDown,
  Wallet,
  Radar,
} from 'lucide-react';
import { useState, type ElementType } from 'react';

interface NavItem {
  label: string;
  href: string;
  icon: ElementType;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: 'Bot',
    items: [
      { label: 'Overview', href: '/', icon: LayoutDashboard },
      { label: 'Strategies', href: '/strategies', icon: Layers },
      { label: 'Wallets', href: '/wallets', icon: Wallet },
      { label: 'Actions', href: '/actions', icon: ArrowLeftRight },
    ],
  },
  {
    label: 'Research',
    items: [{ label: 'Pool Screener', href: '/screener', icon: Radar }],
  },
  {
    label: 'System',
    items: [{ label: 'Settings', href: '/settings', icon: Settings }],
  },
];

export function Sidebar() {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={cn(
        'flex flex-col h-full bg-surface border-r border-border transition-all duration-200',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-border shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center shrink-0">
            <span className="text-primary-foreground text-xs font-bold">aH</span>
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">aHedge</p>
              <p className="text-xs text-muted-foreground truncate">Aerodrome × HL</p>
            </div>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-6">
        {navGroups.map((group) => (
          <div key={group.label}>
            {!collapsed && (
              <p className="px-2 mb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {group.label}
              </p>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive =
                  item.href === '/'
                    ? pathname === '/'
                    : pathname === item.href || pathname.startsWith(item.href + '/');
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      'flex items-center gap-3 px-2 py-2 rounded-md text-sm font-medium transition-colors group',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                      collapsed && 'justify-center',
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {!collapsed && <span className="flex-1">{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Collapse toggle */}
      <div className="p-2 border-t border-border">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 py-2 px-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors text-xs"
        >
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform',
              collapsed ? '-rotate-90' : 'rotate-90',
            )}
          />
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </aside>
  );
}
